import { Injectable, signal, computed } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ApiError } from '../../abstract/api-error';
import { ApiService } from '../../abstract/api.service';
import { isRecord } from '../utils/is-record';
import { SponsorContactData } from '../response-models/sponsor-contact-data.model';

/**
 * Cache entry with timestamp for TTL-based invalidation
 */
interface CacheEntry {
  data: SponsorContactData;
  timestamp: number;
}

/**
 * SponsorContactService
 *
 * Fetches and caches sponsor contact data (locations, representatives, contact info)
 * from the Hub API. Uses client-side caching with a 10-minute TTL keyed by sponsor ID.
 *
 * Signal-based state management following the ChemicalService pattern.
 */
@Injectable({
  providedIn: 'root'
})
export class SponsorContactService extends ApiService {

  /** Cache TTL: 10 minutes */
  private readonly CACHE_TTL_MS = 10 * 60 * 1000;

  /** Client-side cache keyed by sponsor ID */
  private cache = new Map<string, CacheEntry>();

  // Signal-based state
  private loadingSignal = signal<boolean>(false);
  private errorSignal = signal<string | null>(null);
  private contactDataSignal = signal<SponsorContactData | null>(null);

  /** Whether a fetch is currently in progress */
  readonly loading = computed(() => this.loadingSignal());

  /** Error message from the last failed fetch, or null */
  readonly error = computed(() => this.errorSignal());

  /** The most recently fetched sponsor contact data */
  readonly contactData = computed(() => this.contactDataSignal());

  constructor() {
    super('sponsors');
  }

  /**
   * Fetch sponsor contact data by sponsor ID.
   * Returns cached data if a valid (non-expired) entry exists.
   * Otherwise fetches from GET /sponsors/:sponsorId/contacts.
   */
  async fetchContacts(sponsorId: string): Promise<void> {
    if (!sponsorId) {
      this.contactDataSignal.set(null);
      return;
    }

    // Check cache first
    const cached = this.cache.get(sponsorId);
    if (cached && (Date.now() - cached.timestamp) < this.CACHE_TTL_MS) {
      this.contactDataSignal.set(cached.data);
      this.errorSignal.set(null);
      return;
    }

    this.loadingSignal.set(true);
    this.errorSignal.set(null);

    try {
      const data = await firstValueFrom(
        this.request('GET', { id: sponsorId, path: 'contacts' }, raw => (isRecord(raw) ? (raw as unknown as SponsorContactData) : null))
      );
      if (data) {
        this.cache.set(sponsorId, { data, timestamp: Date.now() });
      }
      this.contactDataSignal.set(data);
    } catch (error: unknown) {
      console.error('Error fetching sponsor contacts:', error);
      this.errorSignal.set(ApiError.fromHttpError(error).errors[0] ?? 'Failed to load sponsor contact data');
      this.contactDataSignal.set(null);
    } finally {
      this.loadingSignal.set(false);
    }
  }

  /**
   * Clear the entire cache. Useful for testing or manual invalidation.
   */
  clearCache(): void {
    this.cache.clear();
  }
}
